import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, Home } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ResetPasswordConfirm = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  // Couleur principale du thème
  const themeColor = 'rgb(196, 150, 96)';
  const themeColorHover = 'rgb(176, 130, 76)';

  useEffect(() => {
    if (!token) {
      setError('Lien invalide. Veuillez demander un nouveau lien de réinitialisation.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          new_password: newPassword
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/membre');
        }, 3000);
      } else {
        // Gérer les erreurs correctement
        try {
          const errorData = await response.json();
          if (typeof errorData.detail === 'string') {
            setError(errorData.detail);
          } else if (Array.isArray(errorData.detail)) {
            // Erreur de validation Pydantic
            setError(errorData.detail.map(err => err.msg).join(', '));
          } else {
            setError('Une erreur est survenue');
          }
        } catch {
          setError('Une erreur est survenue');
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Page lien invalide
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 relative" style={{ backgroundColor: '#f8f5f0' }}>
        {/* Bouton Retour au site */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 flex items-center space-x-2 px-4 py-2 bg-white border-2 rounded-lg transition-all hover:bg-gray-50 shadow-md"
          style={{ 
            borderColor: themeColor,
            fontFamily: '"Lora", serif'
          }}
        >
          <Home className="w-4 h-4" style={{ color: themeColor }} />
          <span className="text-sm font-medium" style={{ color: themeColor }}>
            Retour au site
          </span>
        </button>

        <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-lg">
          <div className="text-center">
            <img 
              src="/images/lignages-logo-admin.png" 
              alt="Logo Lignages" 
              className="mx-auto h-16 w-16 object-contain mb-4"
            />
            <h2 className="text-2xl font-bold text-red-600 mb-4" style={{ fontFamily: '"Lora", serif' }}>
              Lien invalide
            </h2>
            <p className="text-gray-600 mb-6">
              Ce lien de réinitialisation est invalide ou a expiré.
            </p>
            <button
              onClick={() => navigate('/reset-password')}
              className="text-white px-6 py-2 rounded-lg transition-all"
              style={{ backgroundColor: themeColor, fontFamily: '"Lora", serif' }}
              onMouseOver={(e) => e.target.style.backgroundColor = themeColorHover}
              onMouseOut={(e) => e.target.style.backgroundColor = themeColor}
            >
              Demander un nouveau lien
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Page succès
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#f8f5f0' }}>
        <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-lg">
          <div className="text-center">
            <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: themeColor }} />
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: '"Lora", serif' }}>
              Mot de passe réinitialisé !
            </h2>
            <p className="text-gray-600 mb-6">
              Votre mot de passe a été réinitialisé avec succès.
            </p>
            <p className="text-sm text-gray-500">
              Redirection vers la page de connexion...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire principal
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative" style={{ backgroundColor: '#f8f5f0' }}>
      
      {/* Bouton Retour au site */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center space-x-2 px-4 py-2 bg-white border-2 rounded-lg transition-all hover:bg-gray-50 shadow-md"
        style={{ 
          borderColor: themeColor,
          fontFamily: '"Lora", serif'
        }}
      >
        <Home className="w-4 h-4" style={{ color: themeColor }} />
        <span className="text-sm font-medium" style={{ color: themeColor }}>
          Retour au site
        </span>
      </button>

      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        
        {/* Header */}
        <div className="text-center">
          <img 
            src="/images/lignages-logo-admin.png" 
            alt="Logo Lignages" 
            className="mx-auto h-16 w-16 object-contain mb-4"
          />
          <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: '"Lora", serif' }}>
            Nouveau mot de passe
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Choisissez un nouveau mot de passe sécurisé
          </p>
        </div>

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Formulaire */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Nouveau mot de passe */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  placeholder="Minimum 8 caractères"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirmer mot de passe */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent"
                  placeholder="Retapez le mot de passe"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Info sécurité */}
          <div className="border rounded-lg p-3" style={{ backgroundColor: 'rgba(196, 150, 96, 0.1)', borderColor: themeColor }}>
            <p className="text-xs" style={{ color: 'rgb(120, 90, 50)' }}>
              🔒 Votre mot de passe doit contenir au moins 8 caractères
            </p>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ 
                backgroundColor: isLoading ? themeColorHover : themeColor,
                fontFamily: '"Lora", serif'
              }}
              onMouseOver={(e) => !isLoading && (e.target.style.backgroundColor = themeColorHover)}
              onMouseOut={(e) => !isLoading && (e.target.style.backgroundColor = themeColor)}
            >
              {isLoading ? (
                <span>Réinitialisation...</span>
              ) : (
                <span>Réinitialiser le mot de passe</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;
